import type {
  ReferralSetting,
  GenericError,
  PayoutRuleData,
  ReferralOneTimeSetting,
  ReferralSettingApiRequest,
} from 'src/types/referral-v2';

import {key, fetching} from 'src/utils/redux';
import type {Dispatch, GetState, ThunkAction} from 'src/reducers';
import {thunkify as flow} from 'src/utils/thunks';
import * as reduxApi from 'src/utils/redux-api';
import {updateProgramDetailsFormData} from 'src/action-creators/referral-v2/programs';
import {DEFAULT_ERROR} from 'src/action-creators/referral-v2/referral-jobs';

import {toastMessage} from 'src/components/referral-v2/tracking/referral-confirm-modal';
import {toastApi} from '@spaced-out/ui-design-system/lib/components/Toast';


export const REFERRAL_SETTINGS_LOADING = 'referral/referral-settings-loading';
export const REFERRAL_SETTINGS = 'referral/referral-settings';
export const REFERRAL_SETTINGS_ERROR = 'referral/referral-settings-error';
export const UPDATE_PAYOUT_RULE = 'referral/update-payout-rule';
export const UPDATE_ONE_TIME_SETTINGS = 'referral/update-one-time-settings';

type ReferralSettingsLoadingAction = {
  type: typeof REFERRAL_SETTINGS_LOADING;
};

type ReferralSettingsAction = {
  type: typeof REFERRAL_SETTINGS;
  payload: ReferralSetting | null | undefined;
};

type ReferralSettingsErrorAction = {
  type: typeof REFERRAL_SETTINGS_ERROR;
  payload: GenericError | null | undefined;
};

type UpdatePayoutRuleAction = {
  type: typeof UPDATE_PAYOUT_RULE;
  payload: PayoutRuleData | null | undefined;
};

type UpdateOneTimeSettingsAction = {
  type: typeof UPDATE_ONE_TIME_SETTINGS;
  payload: ReferralOneTimeSetting;
};

export type ReferralSettingsActions =
  | ReferralSettingsAction
  | ReferralSettingsErrorAction
  | ReferralSettingsLoadingAction
  | UpdatePayoutRuleAction
  | UpdateOneTimeSettingsAction;

const recieveReferralSetting = (
  payload: ReferralSetting,
): ReferralSettingsAction => ({
  type: REFERRAL_SETTINGS,
  payload,
});

const getReferralSettingsLoading = (): ReferralSettingsLoadingAction => ({
  type: REFERRAL_SETTINGS_LOADING,
});

const getReferralSettingsError = (
  payload?: GenericError | null,
): ReferralSettingsErrorAction => ({
  type: REFERRAL_SETTINGS_ERROR,
  payload,
});

export const updatePayoutRule = (
  payload?: PayoutRuleData | null,
): UpdatePayoutRuleAction => ({
  type: UPDATE_PAYOUT_RULE,
  payload,
});

export const updateOneTimeSettings = (
  payload: ReferralOneTimeSetting,
): UpdateOneTimeSettingsAction => ({
  type: UPDATE_ONE_TIME_SETTINGS,
  payload,
});

export const getReferralSettings: () => ThunkAction<void> = flow(
  key(() => `referral/configure/settings`),
  fetching(),
)(() => (dispatch: Dispatch, getState: GetState) => {
  const programDetailsFormData =
    getState().referralProgram.program_details_form_data;
  dispatch(getReferralSettingsLoading());

  return dispatch(reduxApi.get(`referral/configure/settings`)).then(
    // @ts-expect-error - TS2345 - Argument of type '(response: ReferralSetting) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
    (response: ReferralSetting) => {
      dispatch(recieveReferralSetting(response));
      if (response.bonus_and_payout_settings) {
        dispatch(
          updateProgramDetailsFormData({
            ...programDetailsFormData,
            bonus_and_payout_settings: response.bonus_and_payout_settings,
          }),
        );
      }
    },
    (error) => {
      dispatch(
        getReferralSettingsError(error?.response?.errors || DEFAULT_ERROR),
      );
    },
  );
});

export const updateReferralSettings: (
  payload: ReferralSettingApiRequest[],
  t: (arg1: string, arg2: string) => string,
  makeKey: (arg1: string) => string,
) => ThunkAction<void> = flow()(
  (payload, t, makeKey) => async (dispatch: Dispatch) => {
    try {
      await dispatch(
        reduxApi.put('referral/configure/settings', {data: payload}),
      );
      toastApi.show(
        toastMessage({
          message: t(
            'Details_saved_successfully',
            'Details saved successfully',
          ),
        }),
        {
          timeout: 500,
        },
      );
      dispatch(getReferralSettings());
    } catch (error: any) {
      toastApi.show(
        toastMessage({
          message: t(
            makeKey(error?.responseBody?.message),
            error?.responseBody?.message,
          ),
          semantic: 'danger',
        }),
        {
          timeout: 500,
        },
      );
    }
  },
);
